import React, { useState, createElement } from 'react';
import { Plus, Trash2, Edit2, Download, Search } from 'lucide-react';
import { primaryButton, ghostButton, inputStyle, glass } from '../ui';
interface Cliente {
  id: string;
  codigo: string;
  nombre: string;
  telefono: string;
  direccion: string;
  capacidadTanque: number;
}
interface Contrato {
  id: string;
  numeroContrato: string;
  clienteId: string;
  clienteNombre: string;
  fechaInicio: string;
  fechaVencimiento: string;
  precioAcordado: number;
  condiciones: string;
  estado: 'vigente' | 'vencido' | 'cancelado';
  firmaDigital?: string;
  fecha_creacion: string;
}
interface ContratosProps {
  contratos: Contrato[];
  clientes: Cliente[];
  onAddContrato: (contrato: Contrato) => void;
  onDeleteContrato: (id: string) => void;
  onUpdateContrato: (contrato: Contrato) => void;
}
export function Contratos({
  contratos,
  clientes,
  onAddContrato,
  onDeleteContrato,
  onUpdateContrato
}: ContratosProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState<'todos' | 'vigente' | 'vencido' | 'cancelado'>('vigente');
  const [formData, setFormData] = useState<Partial<Contrato>>({
    estado: 'vigente',
    precioAcordado: 0
  });
  const [selectedCliente, setSelectedCliente] = useState<string>('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.numeroContrato || !selectedCliente || !formData.fechaInicio || !formData.fechaVencimiento) {
      alert('Por favor completa los campos obligatorios');
      return;
    }
    const cliente = clientes.find(c => c.id === selectedCliente);
    if (editingId) {
      onUpdateContrato({
        ...formData,
        id: editingId,
        clienteId: selectedCliente,
        clienteNombre: cliente?.nombre || ''
      } as Contrato);
      setEditingId(null);
    } else {
      onAddContrato({
        ...formData,
        id: Date.now().toString(),
        clienteId: selectedCliente,
        clienteNombre: cliente?.nombre || '',
        fecha_creacion: new Date().toISOString().split('T')[0]
      } as Contrato);
    }
    setFormData({
      estado: 'vigente',
      precioAcordado: 0
    });
    setSelectedCliente('');
    setShowForm(false);
  };
  const handleEdit = (contrato: Contrato) => {
    setFormData(contrato);
    setSelectedCliente(contrato.clienteId);
    setEditingId(contrato.id);
    setShowForm(true);
  };
  const filteredContratos = contratos.filter(c => {
    const matchSearch = c.numeroContrato.toLowerCase().includes(searchTerm.toLowerCase()) || c.clienteNombre.toLowerCase().includes(searchTerm.toLowerCase());
    const matchEstado = filterEstado === 'todos' || c.estado === filterEstado;
    return matchSearch && matchEstado;
  });
  const contratosVigentes = contratos.filter(c => c.estado === 'vigente').length;
  const contratosVencidos = contratos.filter(c => c.estado === 'vencido').length;
  const generatePDF = (contrato: Contrato) => {
    const cliente = clientes.find(c => c.id === contrato.clienteId);
    if (!cliente) return;
    const content = `
CONTRATO DE SERVICIOS DE GAS INDUSTRIAL

Número de Contrato: ${contrato.numeroContrato}
Fecha: ${contrato.fechaInicio}

DATOS DEL CLIENTE:
Nombre: ${cliente.nombre}
Dirección: ${cliente.direccion}
Teléfono: ${cliente.telefono}
Capacidad del Tanque: ${cliente.capacidadTanque} galones

TÉRMINOS Y CONDICIONES:
Precio Acordado: RD$ ${contrato.precioAcordado.toFixed(2)}
Fecha de Inicio: ${contrato.fechaInicio}
Fecha de Vencimiento: ${contrato.fechaVencimiento}

Condiciones:
${contrato.condiciones}

Estado: ${contrato.estado}

Generado automáticamente por el Sistema de Gestión de Gas Industrial
    `;
    const blob = new Blob([content], {
      type: 'text/plain'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Contrato_${contrato.numeroContrato}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  return <div className="space-y-6" data-id="element-407">
      {/* Header Stats */}
      <div className="grid grid-cols-3 gap-4" data-id="element-408">
        <div style={glass} data-id="element-409">
          <div className="text-sm text-gray-400" data-id="element-410">Total Contratos</div>
          <div className="text-3xl font-bold text-blue-400" data-id="element-411">
            {contratos.length}
          </div>
        </div>
        <div style={glass} data-id="element-412">
          <div className="text-sm text-gray-400" data-id="element-413">Vigentes</div>
          <div className="text-3xl font-bold text-green-400" data-id="element-414">
            {contratosVigentes}
          </div>
        </div>
        <div style={glass} data-id="element-415">
          <div className="text-sm text-gray-400" data-id="element-416">Vencidos</div>
          <div className="text-3xl font-bold text-red-400" data-id="element-417">
            {contratosVencidos}
          </div>
        </div>
      </div>

      {/* Controles */}
      <div style={glass} data-id="element-418">
        <div className="flex gap-4 flex-wrap" data-id="element-419">
          <div className="flex-1 min-w-[250px] relative" data-id="element-420">
            <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" data-id="element-421" />
            <input type="text" placeholder="Buscar por número o cliente..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{
            ...inputStyle,
            paddingLeft: 38
          }} data-id="element-422" />
          </div>
          <select value={filterEstado} onChange={e => setFilterEstado(e.target.value as any)} style={{
          ...inputStyle,
          width: 'auto'
        }} data-id="element-423">
            <option value="todos" data-id="element-424">Todos</option>
            <option value="vigente" data-id="element-425">Vigentes</option>
            <option value="vencido" data-id="element-426">Vencidos</option>
            <option value="cancelado" data-id="element-427">Cancelados</option>
          </select>
          <button onClick={() => {
          setShowForm(!showForm);
          setEditingId(null);
          setFormData({});
        }} style={primaryButton} data-id="element-428">
            <Plus className="w-4 h-4" data-id="element-429" /> Nuevo Contrato
          </button>
        </div>
      </div>

      {/* Formulario */}
      {showForm && <div style={glass} data-id="element-430">
          <h3 className="text-lg font-bold mb-4" data-id="element-431">
            {editingId ? 'Editar Contrato' : 'Nuevo Contrato'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-432">
            <div className="grid grid-cols-2 gap-4" data-id="element-433">
              <div data-id="element-434">
                <label className="text-sm text-gray-300" data-id="element-435">
                  Número de Contrato *
                </label>
                <input type="text" value={formData.numeroContrato || ''} onChange={e => setFormData({
              ...formData,
              numeroContrato: e.target.value
            })} style={inputStyle} placeholder="CTR-2024-001" data-id="element-436" />
              </div>
              <div data-id="element-437">
                <label className="text-sm text-gray-300" data-id="element-438">Cliente *</label>
                <select value={selectedCliente} onChange={e => setSelectedCliente(e.target.value)} style={inputStyle} data-id="element-439">
                  <option value="" data-id="element-440">Selecciona un cliente</option>
                  {clientes.map(c => <option key={c.id} value={c.id} data-id="element-441">
                      {c.nombre} ({c.codigo})
                    </option>)}
                </select>
              </div>
              <div data-id="element-442">
                <label className="text-sm text-gray-300" data-id="element-443">
                  Fecha de Inicio *
                </label>
                <input type="date" value={formData.fechaInicio || ''} onChange={e => setFormData({
              ...formData,
              fechaInicio: e.target.value
            })} style={inputStyle} data-id="element-444" />
              </div>
              <div data-id="element-445">
                <label className="text-sm text-gray-300" data-id="element-446">
                  Fecha de Vencimiento *
                </label>
                <input type="date" value={formData.fechaVencimiento || ''} onChange={e => setFormData({
              ...formData,
              fechaVencimiento: e.target.value
            })} style={inputStyle} data-id="element-447" />
              </div>
              <div data-id="element-448">
                <label className="text-sm text-gray-300" data-id="element-449">
                  Precio Acordado (RD$)
                </label>
                <input type="number" step="0.01" value={formData.precioAcordado || 0} onChange={e => setFormData({
              ...formData,
              precioAcordado: parseFloat(e.target.value)
            })} style={inputStyle} placeholder="0.00" data-id="element-450" />
              </div>
              <div data-id="element-451">
                <label className="text-sm text-gray-300" data-id="element-452">Estado</label>
                <select value={formData.estado || 'vigente'} onChange={e => setFormData({
              ...formData,
              estado: e.target.value as any
            })} style={inputStyle} data-id="element-453">
                  <option value="vigente" data-id="element-454">Vigente</option>
                  <option value="vencido" data-id="element-455">Vencido</option>
                  <option value="cancelado" data-id="element-456">Cancelado</option>
                </select>
              </div>
              <div className="col-span-2" data-id="element-457">
                <label className="text-sm text-gray-300" data-id="element-458">
                  Condiciones Comerciales
                </label>
                <textarea value={formData.condiciones || ''} onChange={e => setFormData({
              ...formData,
              condiciones: e.target.value
            })} style={inputStyle} rows={4} placeholder="Especifica las condiciones del contrato..." data-id="element-459" />
              </div>
            </div>
            <div className="flex gap-2" data-id="element-460">
              <button type="submit" style={primaryButton} data-id="element-461">
                {editingId ? 'Actualizar' : 'Crear'} Contrato
              </button>
              <button type="button" onClick={() => {
            setShowForm(false);
            setEditingId(null);
          }} style={ghostButton} data-id="element-462">
                Cancelar
              </button>
            </div>
          </form>
        </div>}

      {/* Lista de Contratos */}
      <div style={glass} data-id="element-463">
        <h3 className="text-lg font-bold mb-4" data-id="element-464">
          Contratos ({filteredContratos.length})
        </h3>
        <div className="overflow-x-auto" data-id="element-465">
          <table className="w-full text-sm" data-id="element-466">
            <thead data-id="element-467">
              <tr className="border-b border-gray-700" data-id="element-468">
                <th className="text-left py-2 px-2" data-id="element-469">Nº Contrato</th>
                <th className="text-left py-2 px-2" data-id="element-470">Cliente</th>
                <th className="text-left py-2 px-2" data-id="element-471">Inicio</th>
                <th className="text-left py-2 px-2" data-id="element-472">Vencimiento</th>
                <th className="text-right py-2 px-2" data-id="element-473">Precio (RD$)</th>
                <th className="text-left py-2 px-2" data-id="element-474">Estado</th>
                <th className="text-center py-2 px-2" data-id="element-475">Acciones</th>
              </tr>
            </thead>
            <tbody data-id="element-476">
              {filteredContratos.length === 0 ? <tr data-id="element-477">
                  <td colSpan={7} className="text-center py-4 text-gray-400" data-id="element-478">
                    No hay contratos
                  </td>
                </tr> : filteredContratos.map(contrato => <tr key={contrato.id} className="border-b border-gray-700 hover:bg-gray-800/30" data-id="element-479">
                    <td className="py-2 px-2 font-mono text-blue-400" data-id="element-480">
                      {contrato.numeroContrato}
                    </td>
                    <td className="py-2 px-2" data-id="element-481">{contrato.clienteNombre}</td>
                    <td className="py-2 px-2 text-sm" data-id="element-482">
                      {contrato.fechaInicio}
                    </td>
                    <td className="py-2 px-2 text-sm" data-id="element-483">
                      {contrato.fechaVencimiento}
                    </td>
                    <td className="py-2 px-2 text-right font-semibold" data-id="element-484">
                      {contrato.precioAcordado.toFixed(2)}
                    </td>
                    <td className="py-2 px-2" data-id="element-485">
                      <span className={`text-xs px-2 py-1 rounded ${contrato.estado === 'vigente' ? 'bg-green-900 text-green-200' : contrato.estado === 'vencido' ? 'bg-red-900 text-red-200' : 'bg-gray-900 text-gray-200'}`} data-id="element-486">
                        {contrato.estado}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-center space-x-2" data-id="element-487">
                      <button onClick={() => generatePDF(contrato)} className="text-green-400 hover:text-green-300 inline-block" title="Descargar contrato" data-id="element-488">
                        <Download className="w-4 h-4" data-id="element-489" />
                      </button>
                      <button onClick={() => handleEdit(contrato)} className="text-blue-400 hover:text-blue-300 inline-block" data-id="element-490">
                        <Edit2 className="w-4 h-4" data-id="element-491" />
                      </button>
                      <button onClick={() => onDeleteContrato(contrato.id)} className="text-red-400 hover:text-red-300 inline-block" data-id="element-492">
                        <Trash2 className="w-4 h-4" data-id="element-493" />
                      </button>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}